import { useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import {
  LANGUAGE_CODE_EN_US,
  LanguageCodes,
  SQUARE_IMAGE,
} from 'global/constants';
import { ACCEPT_TYPE_IMAGE } from 'lib/form/file-dropper/file-dropper.constants';
import { DEFAULT_CROP_DATA } from 'lib/form/image-picker/image-picker';
import { FileDropper } from 'lib/form/file-dropper/file-dropper';
import { CropData } from 'lib/form/image-picker/image-picker.constants';
import { useFileDropperContext } from 'lib/form/file-dropper/file-dropper.context';
import { useImageRequestLoader } from 'approot/shared/images/images.hooks';
import VocabularyImagePickerThumbnail, {
  VOCAB_IMAGE_PICKER_WIDTH,
} from './vocabulary-image-picker-thumbnail';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { SimpleModalVerticalActionsStyled } from 'lib/modal/simple-modal.styles';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import TooltipInfo from 'approot/shared/tooltip/tooltip-info';
import { ImageData } from './vocabulary-setting.constants';
import {
  GENERATE_IMAGE,
  GENERATE_IMAGE_ALT_TEXT,
} from 'approot/dictionary/dictionary.graphql';
import type {
  GenerateImageAltTextQuery,
  GenerateImageAltTextQueryVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { getLanguageLabel } from './vocabulary-setting.utils';
import TextInputLimit from 'approot/shared/text-input-limit/text-input-limit';
import type {
  GenerateImageQuery,
  GenerateImageQueryVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import VocabularyImageRegenerateButton from './vocabulary-image-regenerate-button';
import { ImagePickerEditor } from 'lib/form/image-picker/image-picker-thumbnail.style';
import { VocabularyImagePickerBouncer } from './vocabulary-thumbnail.style';
import { InlineServerError } from 'approot/account/server-error';

const IMAGE_KEY = 'imageKey';
const ALT_TEXT_FIELD = 'altText';

type FormData = {
  [IMAGE_KEY]: string;
  [ALT_TEXT_FIELD]: string;
};

type Props = {
  imageData?: ImageData;
  isDisabled?: boolean;
  language?: LanguageCodes;
  fileKey?: string | null;
  onUpdate: (image: ImageData) => void;
  word: string;
  example: string;
};

export const VocabularyThumbnailUpload = ({
  imageData,
  isDisabled,
  language,
  fileKey,
  onUpdate,
  word,
  example,
}: Props) => {
  const [data, setData] = useState<ImageData | undefined>(imageData);

  const { clearFile } = useFileDropperContext(IMAGE_KEY);
  const { showError } = useSnackbar();

  const [isShowFileDropper, setIsShowFileDropper] = useState<boolean>(!fileKey);
  const [loadingCroppedImage, setLoadingCroppedImage] =
    useState<boolean>(false);

  const [generateAltText, { loading: altTextLoading, error: altTextError }] =
    useLoggingLazyQuery<
      GenerateImageAltTextQuery,
      GenerateImageAltTextQueryVariables
    >(GENERATE_IMAGE_ALT_TEXT, {
      fetchPolicy: 'no-cache',
      onCompleted: res => {
        setValue(
          ALT_TEXT_FIELD,
          res.generateImageAltText.altText.substring(0, 255)
        );
      },
    });

  const formMethods = useForm<FormData>({
    defaultValues: {
      [ALT_TEXT_FIELD]: imageData?.alt?.substring(0, 255),
      [IMAGE_KEY]: imageData?.key,
    },
  });

  const {
    formState: { errors },
    register,
    handleSubmit,
    setValue,
  } = formMethods;

  const {
    isLoading: isLoadingImage,
    imageUrl,
    getImageResponsive,
    setImageUrl,
  } = useImageRequestLoader({
    url: imageData?.original,
    onLoad: () => {
      setIsShowFileDropper(false);
    },
  });

  const [
    generateImage,
    { loading: generateImageLoading, error: generateImageError },
  ] = useLoggingLazyQuery<GenerateImageQuery, GenerateImageQueryVariables>(
    GENERATE_IMAGE,
    {
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          sentence: example,
          word,
        },
      },
      onCompleted: res => {
        const { key, url } = res.generateImage;
        setData({
          ...data,
          key,
          fileName: key,
          cropData: undefined,
          original: url,
          imageUrl: url,
        });
        setValue(IMAGE_KEY, key);
        setImageUrl(url);
        setIsShowFileDropper(false);
      },
    }
  );

  const handleFileUploadChange = useCallback(
    (key: string, fileName: string) => {
      getImageResponsive({
        variables: {
          input: {
            key,
            imageType: SQUARE_IMAGE,
          },
        },
      });
      setData(() => ({
        ...data,
        key,
        alt: undefined,
        fileName,
        cropData: DEFAULT_CROP_DATA,
      }));
      setValue(IMAGE_KEY, key, { shouldValidate: true });
      setValue(ALT_TEXT_FIELD, '');
    },
    [data, getImageResponsive, setData, setValue]
  );

  const handleDeleteImage = useCallback(() => {
    setData({
      key: undefined,
      fileName: undefined,
      designId: undefined,
      cropData: undefined,
      original: undefined,
      imageUrl: undefined,
      alt: undefined,
    });
    setValue(IMAGE_KEY, '');
    setValue(ALT_TEXT_FIELD, '');
    setIsShowFileDropper(true);
    clearFile();
    setImageUrl(undefined);
  }, [setIsShowFileDropper, setData, clearFile, setImageUrl, setValue]);

  const onCropChange = useCallback(
    (cropData: CropData) => {
      setData({ ...data, cropData });
    },
    [data, setData]
  );

  const isDisplayImage = !isShowFileDropper && !isLoadingImage;

  const onSubmit = useCallback(
    (formData: FormData) => {
      clearFile();

      setLoadingCroppedImage(true);
      getImageResponsive({
        variables: {
          input: {
            key: data?.key!,
            imageType: SQUARE_IMAGE,
            cropJson: data?.cropData,
          },
        },
      })
        .then(res => {
          if (res.data) {
            onUpdate({
              ...data,
              original: res.data.getImageResponsive.original,
              imageUrl: res.data.getImageResponsive.responsive[0].urls[0].url,
              alt: formData[ALT_TEXT_FIELD],
              key: formData[IMAGE_KEY],
            });
          } else {
            showError(
              'There was an error updating the image. Please try again.'
            );
            setLoadingCroppedImage(false);
          }
        })
        .catch(() => {
          setLoadingCroppedImage(false);
          showError('There was an error updating the image. Please try again.');
        });
    },
    [data, clearFile, onUpdate, getImageResponsive, showError]
  );

  useEffect(() => {
    register(IMAGE_KEY, {
      required: 'Please upload an image',
    });
  }, [register]);

  return (
    <FormProvider {...formMethods}>
      <form>
        <SettingsGroup>
          {isShowFileDropper && (
            <>
              {generateImageLoading ? (
                <ImagePickerEditor
                  $width={VOCAB_IMAGE_PICKER_WIDTH}
                  $height={VOCAB_IMAGE_PICKER_WIDTH}
                >
                  <VocabularyImagePickerBouncer />
                </ImagePickerEditor>
              ) : (
                <FileDropper
                  square
                  disabled={isDisabled}
                  accept={[ACCEPT_TYPE_IMAGE]}
                  name={IMAGE_KEY}
                  inputDataSelector={IMAGE_KEY}
                  validationError={errors[IMAGE_KEY]?.message}
                  isFinishing={isLoadingImage}
                  onChange={handleFileUploadChange}
                  onFileRemoved={handleDeleteImage}
                />
              )}

              {language === LANGUAGE_CODE_EN_US && (
                <VocabularyImageRegenerateButton
                  onClick={generateImage}
                  disabled={generateImageLoading || altTextLoading}
                  error={!!generateImageError}
                  $marginBottom={0}
                  label="Generate AI Image ✨"
                  loading={generateImageLoading}
                  data-selector="btn-generate-ai-image"
                />
              )}
            </>
          )}
          {isDisplayImage && (
            <VocabularyImagePickerThumbnail
              imageUrl={imageUrl}
              cropValue={data?.cropData}
              isDisabled={isDisabled}
              disabledTooltip="Only English example images can be edited or removed"
              onDeleteImage={handleDeleteImage}
              onCropChange={onCropChange}
              onRegenerateImage={generateImage}
              generateImageLoading={generateImageLoading}
              altTextLoading={altTextLoading}
              error={!!generateImageError}
              language={language}
            />
          )}
        </SettingsGroup>

        <SettingsGroup marginBottom={0}>
          <SettingsLabel htmlFor={ALT_TEXT_FIELD}>
            {language && language !== LANGUAGE_CODE_EN_US
              ? `${getLanguageLabel(language)} Alt Text`
              : 'Alt Text'}
            <TooltipInfo
              multiline
              hoverHeading="What is Alt Text?"
              hoverText="Alt text (alternative text) provides a textual description of an image for people who are blind or have low vision. It should concisely convey the subjects, setting, actions, and other relevant details in the image, enabling these users to understand its content and context."
            />
          </SettingsLabel>
          <TextInputLimit
            rows={3}
            maxLength={255}
            validation={{
              required: 'Please add some Alt Text.',
            }}
            required
            id={ALT_TEXT_FIELD}
            name={ALT_TEXT_FIELD}
            placeholder={
              altTextLoading ? 'Generating Alt Text...' : 'Describe this image'
            }
            onChange={alt => {
              setData(() => ({
                ...data,
                alt,
              }));
            }}
            isDisabled={altTextLoading}
          />
        </SettingsGroup>
        <SimpleModalVerticalActionsStyled>
          <SecondaryLoadingButton
            data-selector="generate-alt-text"
            type="button"
            isLoading={altTextLoading}
            isDisabled={altTextLoading || generateImageLoading || !imageUrl}
            aria-disabled={altTextLoading || !imageUrl}
            onClick={() => {
              setValue(ALT_TEXT_FIELD, '');
              generateAltText({
                variables: {
                  input: {
                    imageUrl: imageUrl || '',
                  },
                },
              });
            }}
          >
            {altTextLoading ? 'Generating Alt Text' : 'Generate Alt Text ✨'}
          </SecondaryLoadingButton>
          <PrimaryButton
            type="button"
            loading={loadingCroppedImage}
            disabled={
              !imageUrl ||
              altTextLoading ||
              generateImageLoading ||
              loadingCroppedImage
            }
            onClick={handleSubmit(onSubmit)}
            data-selector="modal-btn-save"
          >
            Save
          </PrimaryButton>
          {altTextError && (
            <InlineServerError
              centreText
              customError="The request timed out. Please try again."
            />
          )}
        </SimpleModalVerticalActionsStyled>
      </form>
    </FormProvider>
  );
};
